import { useMemo, useState } from 'react';
import {
  InfoOutline,
  MenuIcon,
  MultiSelect,
  PricingIcon,
  SaladIcon,
  StatusBar,
  Typography,
} from '@weareopenr/component-library';
import { ItemType } from 'src/constants';
import { ValidationErrorKey } from 'src/api/getValidationReport/types';
import type {
  IValidationReportItem,
  IValidationReportItemOptionGroup,
  IValidationReportMenuRef,
  IValidationReportPricing,
  ValidationItemType,
} from 'src/api/getValidationReport/types';
import { useValidationReport } from 'src/hooks/useValidationReport';
import { usePriceSchemes } from 'src/hooks/usePriceSchemes';
import { useUpdateNotification } from 'src/hooks/useUpdateNotification/useUpdateNotification';
import { NotificationType } from 'src/api/getNotifications/getNotifications';
import { ValidationReportModal } from 'src/components/ValidationReportModal';
import type { IValidationReportSection } from 'src/components/ValidationReportModal';
import styles from './ValidationReportContainer.module.scss';

const QUICK_NOTE = (
  <span>
    <strong>Quick note</strong> - This report flags missing information which may lead to a publish failure. Please
    review the relevant warnings and amend required details by deleting this schedule and recreating.
  </span>
);

const TIMETABLE_QUICK_NOTE = (
  <span>
    <strong>Quick note</strong> - If all required data is not present at the point of publish, this will cause a publish
    failure. Review the validation types required for your channel using the validation filter above.
  </span>
);

const getFooterNote = (isTimetableReport: boolean, isMenuReport: boolean) => {
  if (isTimetableReport) return TIMETABLE_QUICK_NOTE;
  return isMenuReport ? QUICK_NOTE : undefined;
};

const ERROR_KEY_LABELS: Record<ValidationErrorKey, string> = {
  missing_options_in_group: 'Missing Items',
  missing_option_group: 'Missing Bundle Groups',
  missing_pos_id: 'Missing POS ID',
  missing_default_price: 'Missing Default Price',
  missing_tax_rate: 'Missing Tax Rate',
  missing_description: 'Missing description',
  no_menu_image: 'Missing menu image',
  missing_items_in_section: 'Empty section',
  gap_in_schedule: 'Gap in schedule',
};

// Option groups can be assigned to more than one item, so a handful of
// errors read as plural ("(s)") in that context instead of singular.
const OPTION_GROUP_ERROR_KEY_LABELS: Partial<Record<ValidationErrorKey, string>> = {
  missing_pos_id: 'Missing POS ID(s)',
  missing_default_price: 'Missing Default Price (s)',
  missing_tax_rate: 'Missing Tax Rate (s)',
};

const ITEM_TYPE_LABELS: Record<ValidationItemType, string> = {
  [ItemType.STANDARD]: 'Standard',
  [ItemType.MODIFIER]: 'Modifier',
  [ItemType.BUNDLE]: 'Bundle',
  'NON-FOOD': 'Non-food',
  MODIFIER_GROUP: 'Modifier Group',
  BUNDLE_GROUP: 'Bundle Group',
};

const TYPE_OPTIONS = Object.entries(ITEM_TYPE_LABELS).map(([id, label], index) => ({
  id,
  label,
  display_order: index,
}));

const mapErrors = (errors: ValidationErrorKey[], isOptionGroup = false) =>
  errors.map(
    (error) => (isOptionGroup ? OPTION_GROUP_ERROR_KEY_LABELS[error] : undefined) ?? ERROR_KEY_LABELS[error] ?? error,
  );

// Every error key on the row, including those from any price scheme - used
// for filtering, so each key appears once.
const flatErrors = (errors: ValidationErrorKey[], pricing: IValidationReportPricing[]): ValidationErrorKey[] =>
  Array.from(new Set([...errors, ...pricing.flatMap((scheme) => scheme.errors)]));

// Display labels for the row. A timetable report validates against all of its
// price schemes, so each pricing error names its scheme ("Missing Default Price
// in Weekend"). Menu reports use one scheme, which is already in the header.
const mapRowErrors = (
  errors: ValidationErrorKey[],
  pricing: IValidationReportPricing[],
  isOptionGroup: boolean,
  namePriceSchemes: boolean,
) => [
  ...mapErrors(errors, isOptionGroup),
  ...pricing.flatMap((scheme) =>
    mapErrors(scheme.errors, isOptionGroup).map((label) => (namePriceSchemes ? `${label} in ${scheme.name}` : label)),
  ),
];

// Filter options built from the report itself, so they only ever list the
// timetable's own menus and price schemes
const uniqueOptions = (entries: { id: string; label: string }[]) =>
  Array.from(new Map(entries.map((entry) => [entry.id, entry])).values())
    .sort((a, b) => a.label.localeCompare(b.label))
    .map((entry, index) => ({ ...entry, display_order: index }));

// Timetable and menu set reports cover several menus, so each row says which menus it's on
const MenusLine = ({ menus }: { menus: IValidationReportMenuRef[] }) =>
  menus.length === 0 ? null : (
    <Typography size="xsmall" weight="regular" className={styles['used-in-line']}>
      <strong>Menus used in</strong> - {menus.map((menu) => menu.name).join(', ')}
    </Typography>
  );

const mapItemToSection = (
  item: IValidationReportItem,
  showMenus: boolean,
  namePriceSchemes: boolean,
): IValidationReportSection => {
  const itemMenus = item.menus ?? [];
  const rangeNames = item.ranges.map((range) => range.name).join(', ') || undefined;

  return {
    key: item.item_id,
    title: item.name,
    subtitle: rangeNames ? (
      <Typography size="xsmall" weight="regular" className={styles['range-subtitle']}>
        {rangeNames}
      </Typography>
    ) : undefined,
    meta: ITEM_TYPE_LABELS[item.item_type],
    errors: mapRowErrors(item.errors, item.pricing, false, namePriceSchemes || item.pricing.length > 1),
    extra:
      showMenus && itemMenus.length > 0 ? (
        <div className={styles['used-in']}>
          <MenusLine menus={itemMenus} />
        </div>
      ) : undefined,
  };
};

const mapOptionGroupToSection = (
  group: IValidationReportItemOptionGroup,
  showMenus: boolean,
  namePriceSchemes: boolean,
): IValidationReportSection => {
  const productsUsedIn = group.assigned_to.map((assignment) => assignment.name).join(', ');
  const groupMenus = group.menus ?? [];
  const hasMenus = showMenus && groupMenus.length > 0;

  return {
    key: group.option_group_id,
    title: group.name,
    meta: ITEM_TYPE_LABELS[group.option_group_type],
    errors: mapRowErrors(group.errors, group.pricing, true, namePriceSchemes || group.pricing.length > 1),
    extra:
      productsUsedIn || hasMenus ? (
        <div className={styles['used-in']}>
          {productsUsedIn && (
            <Typography size="xsmall" weight="regular" className={styles['used-in-line']}>
              <strong>Products used in</strong> - {productsUsedIn}
            </Typography>
          )}
          {hasMenus && <MenusLine menus={groupMenus} />}
        </div>
      ) : undefined,
  };
};

interface IContentProps {
  reportId: string;
  closeModal: () => void;
  isTimetableReport?: boolean;
}

const Content = ({ reportId, closeModal, isTimetableReport: isKnownTimetableReport = false }: IContentProps) => {
  const [selectedTypes, setSelectedTypes] = useState<ValidationItemType[]>([]);
  const [selectedErrorTypes, setSelectedErrorTypes] = useState<ValidationErrorKey[]>([]);
  const [selectedMenuIds, setSelectedMenuIds] = useState<string[]>([]);
  const [selectedPriceSchemeIds, setSelectedPriceSchemeIds] = useState<string[]>([]);
  const { data, isLoading, isError } = useValidationReport(reportId);
  const { data: priceSchemes } = usePriceSchemes();

  const priceSchemeName = useMemo(
    () => priceSchemes?.find((scheme) => scheme.id === data?.criteria.price_scheme_id)?.name,
    [data, priceSchemes],
  );

  // Older schedule-triggered reports don't record timetable_id in their criteria, so callers that know the
  // report is for a timetable can say so
  const isTimetableReport = isKnownTimetableReport || Boolean(data?.criteria.timetable_id);
  // A menu set is validated as its member menus against one price scheme, so it lists menus like a
  // timetable report but names its price scheme in the header like a menu report
  const isMenuSetReport = Boolean(data?.criteria.menu_set_id);
  const isMultiMenuReport = isTimetableReport || isMenuSetReport;

  const matchesErrorFilter = (errors: ValidationErrorKey[], pricing: IValidationReportPricing[]) =>
    selectedErrorTypes.length === 0 || selectedErrorTypes.some((e) => flatErrors(errors, pricing).includes(e));

  const matchesMenuFilter = (menus: IValidationReportMenuRef[]) =>
    selectedMenuIds.length === 0 || menus.some((menu) => selectedMenuIds.includes(menu.menu_id));

  // Rows with a missing price in any of the selected price schemes
  const matchesPriceSchemeFilter = (pricing: IValidationReportPricing[]) =>
    selectedPriceSchemeIds.length === 0 ||
    pricing.some((scheme) => selectedPriceSchemeIds.includes(scheme.price_scheme_id) && scheme.errors.length > 0);

  // Every menu the report covers: menu-level results (present when menu rules ran) plus the menus
  // the listed products are on
  // With price schemes selected, rows only list the price errors for those schemes - otherwise a product
  // missing prices in every scheme looks unchanged by the filter
  const visiblePricing = (pricing: IValidationReportPricing[]) =>
    selectedPriceSchemeIds.length === 0
      ? pricing
      : pricing.filter((scheme) => selectedPriceSchemeIds.includes(scheme.price_scheme_id));

  const menuOptions = useMemo(
    () =>
      uniqueOptions(
        [
          ...(data?.menus ?? []),
          ...[...(data?.items ?? []), ...(data?.option_groups ?? [])].flatMap((row) => row.menus ?? []),
        ].map((menu) => ({ id: menu.menu_id, label: menu.name })),
      ),
    [data],
  );

  const priceSchemeOptions = useMemo(
    () =>
      uniqueOptions(
        [...(data?.items ?? []), ...(data?.option_groups ?? [])].flatMap((row) =>
          row.pricing.map((scheme) => ({ id: scheme.price_scheme_id, label: scheme.name })),
        ),
      ),
    [data],
  );

  // Only offer error types that actually occur among the items/option groups
  // rendered here - data.error_summary covers the whole report (including
  // menu/timetable errors we don't display), which would let someone select
  // a filter that can never match anything currently on screen.
  const errorTypeOptions = useMemo(() => {
    if (!data) return [];

    const presentErrors = new Set<ValidationErrorKey>();
    (data.items ?? []).forEach((item) =>
      flatErrors(item.errors, item.pricing).forEach((error) => presentErrors.add(error)),
    );
    (data.option_groups ?? []).forEach((group) =>
      flatErrors(group.errors, group.pricing).forEach((error) => presentErrors.add(error)),
    );

    return Array.from(presentErrors).map((error, index) => ({
      id: error,
      label: ERROR_KEY_LABELS[error] ?? error,
      display_order: index,
    }));
  }, [data]);

  const sections = useMemo(() => {
    if (!data) return [];

    const items = (data.items ?? [])
      .filter(
        (item) =>
          (selectedTypes.length === 0 || selectedTypes.includes(item.item_type)) &&
          matchesErrorFilter(item.errors, item.pricing) &&
          matchesMenuFilter(item.menus ?? []) &&
          matchesPriceSchemeFilter(item.pricing),
      )
      .map((item) =>
        mapItemToSection({ ...item, pricing: visiblePricing(item.pricing) }, isMultiMenuReport, isTimetableReport),
      );

    const optionGroups = (data.option_groups ?? [])
      .filter(
        (group) =>
          (selectedTypes.length === 0 || selectedTypes.includes(group.option_group_type)) &&
          matchesErrorFilter(group.errors, group.pricing) &&
          matchesMenuFilter(group.menus ?? []) &&
          matchesPriceSchemeFilter(group.pricing),
      )
      .map((group) =>
        mapOptionGroupToSection(
          { ...group, pricing: visiblePricing(group.pricing) },
          isMultiMenuReport,
          isTimetableReport,
        ),
      );

    return [...items, ...optionGroups];
  }, [
    data,
    isTimetableReport,
    isMultiMenuReport,
    selectedTypes,
    selectedErrorTypes,
    selectedMenuIds,
    selectedPriceSchemeIds,
  ]);

  const footerNote = getFooterNote(isTimetableReport, Boolean(data?.criteria.menu_id ?? data?.criteria.menu_set_id));

  return (
    <ValidationReportModal
      isOpen
      closeModal={closeModal}
      sections={sections}
      isLoading={isLoading}
      isError={isError}
      headerExtra={
        priceSchemeName ? (
          <Typography weight="regular">
            Price Scheme - <strong>{priceSchemeName}</strong>
          </Typography>
        ) : undefined
      }
      filters={
        <>
          {isMultiMenuReport && (
            <MultiSelect
              Icon={MenuIcon}
              label="Menu"
              options={menuOptions}
              selected={selectedMenuIds}
              onChange={(vals) => setSelectedMenuIds(vals as string[])}
              disabled={isLoading}
            />
          )}
          {isTimetableReport && (
            <MultiSelect
              Icon={PricingIcon}
              label="Price Scheme"
              options={priceSchemeOptions}
              selected={selectedPriceSchemeIds}
              onChange={(vals) => setSelectedPriceSchemeIds(vals as string[])}
              disabled={isLoading}
            />
          )}
          <MultiSelect
            Icon={SaladIcon}
            label="Product Type"
            options={TYPE_OPTIONS}
            selected={selectedTypes}
            onChange={(vals) => setSelectedTypes(vals as ValidationItemType[])}
            disabled={isLoading}
          />
          <MultiSelect
            Icon={InfoOutline}
            label="Validation Type"
            options={errorTypeOptions}
            selected={selectedErrorTypes}
            onChange={(vals) => setSelectedErrorTypes(vals as ValidationErrorKey[])}
            disabled={isLoading}
          />
        </>
      }
      // This modal is shared by menu, menu set, timetable, product, and pricing reports. Menu and menu set reports
      // share a note, timetable reports get their own; product and pricing reports have none.
      footerExtra={footerNote ? <StatusBar message={footerNote} variant="info" /> : undefined}
    />
  );
};

interface IValidationReportContainerProps {
  isOpen: boolean;
  closeModal: () => void;
  reportId: string;
  isTimetableReport?: boolean;
}

export const ValidationReportContainer = ({
  isOpen,
  closeModal,
  reportId,
  isTimetableReport,
}: IValidationReportContainerProps) => {
  const { markNotificationRead } = useUpdateNotification();

  const handleClose = () => {
    markNotificationRead(
      (notification) => notification.type === NotificationType.VALIDATION && notification.report_id === reportId,
    );
    closeModal();
  };

  return isOpen ? <Content reportId={reportId} closeModal={handleClose} isTimetableReport={isTimetableReport} /> : null;
};
